import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { SBOM_PORTAL } from '../../../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../../../typings/permissions';
import { window } from '../../../../../globals';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import { FeatureFlagRequired } from '../../../../generic';
import ReportPreview from '../shared/components/Preview';
import ReportStatus from '../shared/components/Preview/ReportStatus';
import { ProjectType } from '../utils';
import type { ProjectGroupContext, ReportCategory, ReportExclusionOptions, ReportGenerationParams } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption, IncludeFixPlanFilterOption } from '../shared/types';

import { getPreviewURL } from './utils';

type Props = {
  params: ReportGenerationParams;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  fixPlanOptions: IncludeFixPlanFilterOption[];
  excludeOptions?: ReportExclusionOptions;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
};

const Preview: VoidFunctionComponent<Props> = ({
  params: { report_type: reportType },
  format,
  includeOptions,
  dependencyMetadataOptions,
  fixPlanOptions,
  excludeOptions,
  authorName,
  authorEmail,
  supplier,
}: Props) => {
  const reportContext = useSelector(ReportGenerationStore.selectors.getReportContext) as ProjectGroupContext;
  const hasPermissionToEditPortalSettings = window.hasPermission({
    resourceType: ResourceType.OrganizationPortalReports,
    action: Action.Manage,
  });
  const endpoint = getPreviewURL({
    groupId: reportContext.groupId,
    releaseId: reportContext.releaseId,
    reportType,
    format,
    includeOptions,
    dependencyMetadataOptions,
    fixPlanOptions,
    excludeOptions,
    authorName,
    authorEmail,
    supplier,
  });

  return (
    <ReportPreview
      reportType={reportType as ReportCategory}
      previewURL={endpoint}
      format={format}
      includeOptions={includeOptions}
      dependencyMetadataOptions={dependencyMetadataOptions}
      publishStatus={(
        reportContext.projectType === ProjectType.ReleaseGroup &&
          <FeatureFlagRequired flagName={SBOM_PORTAL}>
            {hasPermissionToEditPortalSettings && <ReportStatus format={format} reportType={reportType} />}
          </FeatureFlagRequired>
      )}
    />
  );
};

export default Preview;
